import { useState } from "react";
import { Link } from "react-router";
import { Briefcase, CalendarDays, MapPin, Users, Video } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../primitives/card";
import { Badge } from "../../primitives/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../primitives/dropdown-menu";
import { Separator } from "../../primitives/separator";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { isRouteEnabled, paths } from "@shared-core/routing/routes";
import { isOverdue } from "@shared-core/domain/deadline";
import type {
  TicketItem,
  UpcomingMeetingItem,
  WorkAlertItem,
} from "@shared-core/data/api-types";
import { formatClockTime } from "./formatRelativeTime";
import { formatWeekdayDateOrDash } from "@shared-core/domain/formatters";
import { formatDateWithYear } from "./BoardSectionShell";

function isTicketOverdue(dueDate: string | null): boolean {
  return dueDate !== null && isOverdue(dueDate);
}

// Tickets can run long — cap the default view and let the CSR expand in
// place, mirroring the board sections' preview-then-expand pattern
// (see BoardSectionShell.tsx's BOARD_SECTION_PREVIEW_COUNT).
const TICKETS_PREVIEW_COUNT = 5;

/**
 * The CSR home's right rail: active tickets, upcoming meetings, and system
 * alerts — all sourced directly from the composed `CsrHomeDashboard` (see
 * CsrHome.tsx), no client-side join. Tickets lead the rail (the owner's
 * highest-priority item); orders stuck without a production job moved to
 * the left column alongside the board — see CsrHome.tsx.
 */
export function CommandCentreRail({
  upcomingMeetings,
  tickets,
  alerts,
}: {
  upcomingMeetings: UpcomingMeetingItem[];
  tickets: TicketItem[];
  alerts: WorkAlertItem[];
}) {
  const [ticketsExpanded, setTicketsExpanded] = useState(false);
  const hasMoreTickets = tickets.length > TICKETS_PREVIEW_COUNT;
  const visibleTickets =
    ticketsExpanded || !hasMoreTickets
      ? tickets
      : tickets.slice(0, TICKETS_PREVIEW_COUNT);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">
            Tickets
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-0">
          {tickets.length === 0 ? (
            <p className="text-sm text-muted-foreground">No active tickets.</p>
          ) : (
            <>
              {visibleTickets.map((ticket, i) => (
                <div key={ticket.id}>
                  {i > 0 && <Separator className="my-2" />}
                  {/* The row navigates to the ticket on click of its own
                   *  text; the linked job's number is a non-nested sibling
                   *  link (same pattern as PortfolioBoard's BoardJobRow) —
                   *  a job link inside the ticket Link would be an invalid
                   *  nested <a>. */}
                  <Link to={ticket.self_url} className="block text-sm group">
                    <span className="font-medium group-hover:underline">
                      {ticket.title}
                    </span>
                    <div className="flex items-center gap-2 text-xs mt-0.5">
                      {ticket.customer_name && (
                        <span className="text-muted-foreground">
                          {ticket.customer_name}
                        </span>
                      )}
                      {ticket.due_date && (
                        <span
                          className={
                            isTicketOverdue(ticket.due_date)
                              ? "text-red-600 font-medium"
                              : "text-muted-foreground"
                          }
                        >
                          Due {formatDateWithYear(ticket.due_date)}
                        </span>
                      )}
                    </div>
                  </Link>
                  {ticket.job_no && ticket.job_url && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      <Link
                        to={ticket.job_url}
                        className="tabular-nums hover:underline"
                      >
                        #{ticket.job_no}
                      </Link>
                      {ticket.job_due_at && (
                        <span>
                          Needed by {formatDateWithYear(ticket.job_due_at)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {hasMoreTickets && (
                <button
                  type="button"
                  onClick={() => setTicketsExpanded((current) => !current)}
                  className="mt-2 text-sm text-primary hover:underline"
                >
                  {ticketsExpanded
                    ? "Show less"
                    : `Show all ${tickets.length} →`}
                </button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">
            Upcoming meetings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {upcomingMeetings.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No upcoming meetings.
            </p>
          ) : (
            upcomingMeetings.map((event) => (
              <UpcomingMeetingRow key={event.id} event={event} />
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">
            Alerts
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-0">
          {alerts.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No active system alerts.
            </p>
          ) : (
            alerts.map((alert, i) => (
              <div key={alert.id}>
                {i > 0 && <Separator className="my-2" />}
                <Link to={alert.self_url} className="block text-sm group">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium group-hover:underline truncate">
                      #{alert.job_no} {alert.job_title}
                    </span>
                    <Badge
                      variant={alert.severity === "red" ? "error" : "warning"}
                    >
                      {alert.severity}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {alert.message}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Since{" "}
                    {formatDistanceToNow(new Date(alert.detected_at), {
                      addSuffix: true,
                    })}
                    {alert.required_at && (
                      <> · Due {formatWeekdayDateOrDash(alert.required_at)}</>
                    )}
                  </p>
                </Link>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * One upcoming-meeting row. The whole row is the menu trigger — the CSR's
 * next action on a meeting is almost always "open it" or "join it", and
 * neither belongs behind a separate icon target in a rail this narrow.
 *
 * Actions are driven entirely by what capture recorded: `web_link` and
 * `online_meeting_join_url` are both nullable (an event with no online
 * component has no join url; an event captured before those columns existed
 * has neither), and a meeting with no job link has no job to open. With no
 * action available the row renders as plain text rather than a dead button.
 */
function UpcomingMeetingRow({ event }: { event: UpcomingMeetingItem }) {
  const capabilities = usePlatformCapabilities();
  const canOpenJob = Boolean(event.job_no) && isRouteEnabled("jobWorkspace");
  const hasActions = Boolean(
    event.web_link || event.online_meeting_join_url || canOpenJob,
  );

  const summary = (
    <>
      <div className="flex items-start justify-between gap-2">
        <span className="font-medium">
          {formatWeekdayDateOrDash(event.start_at)},{" "}
          {formatClockTime(event.start_at)}
        </span>
        {event.job_no && <Badge variant="neutral">#{event.job_no}</Badge>}
      </div>
      <p className="text-sm">
        {event.title || (
          <span className="text-muted-foreground italic">No title</span>
        )}
      </p>
      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
        {event.location && (
          <span className="inline-flex items-center gap-1">
            <MapPin className="size-3" />
            {event.location}
          </span>
        )}
        <span className="inline-flex items-center gap-1">
          <Users className="size-3" />
          {event.attendee_count}
        </span>
      </div>
    </>
  );

  if (!hasActions) {
    return <div className="text-sm">{summary}</div>;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="w-full text-left text-sm rounded-sm hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {summary}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {event.online_meeting_join_url && (
          <DropdownMenuItem
            onClick={() =>
              capabilities.openExternalUrl(event.online_meeting_join_url!)
            }
          >
            <Video className="size-4" />
            Join meeting
          </DropdownMenuItem>
        )}
        {event.web_link && (
          <DropdownMenuItem
            onClick={() => capabilities.openExternalUrl(event.web_link!)}
          >
            <CalendarDays className="size-4" />
            Open in Outlook
          </DropdownMenuItem>
        )}
        {canOpenJob && (
          <DropdownMenuItem asChild>
            <Link to={paths.jobWorkspace({ jobNo: event.job_no! })}>
              <Briefcase className="size-4" />
              Open job #{event.job_no}
            </Link>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
